import moment from "moment";
import {
  WORK_LOCATIONS,
  formatDay,
  sampleCodes,
  sampleVersions,
} from "pages/PayRoll/Configuration/StatutoryConfiguration/ESI/esiConfigData";

// Payroll > Statutory > ESI > Eligibility, on sample data until the ESI
// APIs exist (ESI Specification v1.7 §4, screens E1–E4). Coverage is
// worked out here from the ESI Configuration sample with the spec's rule
// order; the backend coverage job will do this later.

export { formatDay };

// Sample "today": the end of the Apr–Sep 2026 period, Aug payroll
// finalised (so Sep 2026 is the first open month).
export const AS_OF = "2026-09-30";
export const FIRST_OPEN_MONTH = "2026-09-01";
export const IP_DAYS = 10;

const day = (value) => moment(value, "YYYY-MM-DD");
const iso = (value) => moment(value).format("YYYY-MM-DD");
export const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

// ---- Settings and periods ----

// The ESI version in force on a date (drafts never count).
export const settingsOn = (date) => {
  const saved = sampleVersions().filter((version) => version.status !== "Draft");
  return (
    saved
      .filter((version) => !day(version.effective_from).isAfter(day(date)))
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || saved[0]
  );
};

const periodName = (start, end) =>
  start.year() === end.year() ? `${start.format("MMM")} – ${end.format("MMM YYYY")}` : `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}`;
const shortPeriodName = (start, end) => `${start.format("MMM")}–${end.format("MMM YYYY")}`;

export const PERIODS = ["2025-10-01", "2026-04-01", "2026-10-01"].map((startText) => {
  const start = day(startText);
  const end = start.clone().add(5, "months").endOf("month");
  return {
    value: start.format("YYYY-MM"),
    start: iso(start),
    end: iso(end),
    label: periodName(start, end),
    short: shortPeriodName(start, end),
    tested: !start.isAfter(day(AS_OF)),
  };
});
// Periods that have started can be picked; the current one is the default.
export const PERIOD_OPTIONS = PERIODS.filter((period) => period.tested).map((period) => ({ value: period.value, label: period.label }));
export const CURRENT_PERIOD = PERIODS.filter((period) => period.tested).slice(-1)[0].value;
export const periodOf = (value) => PERIODS.find((period) => period.value === value);
// A period is locked once a payroll of one of its months is finalised.
export const periodLocked = (period) => day(period.start).isBefore(day(FIRST_OPEN_MONTH));

// ---- Employer codes ----

export const CODES = sampleCodes();
// The name used for a code in lists (mockup: "Erode Plant", "Chennai Branch").
export const codeName = (code) => (code ? (code.type === "MAIN" ? code.locations[0] : code.branch_name) : "Not linked");
// Month-end rule: the code of the location on the last day of the month.
export const codeForLocation = (location) => CODES.find((code) => code.status === "Active" && code.locations.includes(location)) || null;
export const CODE_OPTIONS = [
  { value: "all", label: "All codes" },
  ...CODES.filter((code) => code.status === "Active").map((code) => ({ value: code.code, label: `${codeName(code)} · ${code.code}` })),
  { value: "none", label: "Not linked" },
];
export const unlinkedLocations = () =>
  WORK_LOCATIONS.filter((location) => location.employees > 0 && !codeForLocation(location.name)).map((location) => location.name);

// ---- Employees ----

const person = (emp_id, name, department, location, doj, wages, extra = {}) => ({
  id: emp_id,
  emp_id,
  name,
  department,
  location,
  doj,
  lwd: null,
  exit_reason: "",
  ip_number: "",
  dispensary: "",
  disability: null,
  override: null,
  audit: [],
  // Monthly-rate ESI eligibility wage from each date (BASIC + DA + SPECIAL).
  wages: Array.isArray(wages) ? wages : [{ from: doj, amount: wages }],
  ...extra,
});

const NAMED = [
  person("E001", "Ravi Kumar", "Production", "Erode Plant", "2019-06-03", 18000, { ip_number: "6312345670", dispensary: "Erode" }),
  person(
    "E002",
    "Anjali Sharma",
    "Production",
    "Erode Plant",
    "2021-01-04",
    [
      { from: "2021-01-04", amount: 19500 },
      { from: "2025-04-01", amount: 20800 },
      { from: "2026-07-01", amount: 22500 },
    ],
    { ip_number: "6312345671", dispensary: "Erode" },
  ),
  person("E011", "Meera Sharma", "HR", "Erode Office", "2020-02-17", 22800),
  person("E003", "Suresh B", "Finance", "Chennai Office", "2018-11-12", 19600, { ip_number: "6312345672", dispensary: "Ayanavaram" }),
  person("E009", "Manish T", "Production", "Erode Plant", "2026-08-10", 16000, { dispensary: "Erode" }),
  person("E014", "Gopal R", "Quality", "Erode Plant", "2017-07-01", 23400, {
    ip_number: "6312345679",
    dispensary: "Erode",
    disability: { from: "2024-01-01", evidence: "gopal_disability_certificate.pdf" },
  }),
  person("E012", "Reena P", "HR", "Chennai Office", "2026-09-30", 17200),
  person("E005", "Sunil Patel", "Production", "Erode Plant", "2016-03-21", 24000),
  person("E010", "Divya R", "Production", "Erode Plant", "2022-05-09", 18900, { ip_number: "6312345677", dispensary: "Erode" }),
  person("E006", "Kavitha R", "Quality", "Erode Plant", "2020-09-14", 20500, { ip_number: "6312345678", dispensary: "Erode" }),
  person("E013", "Sanjay R", "Production", "Erode Plant", "2026-09-28", 15000),
  person("E015", "Karthik S", "Production", "Erode Plant", "2022-01-10", 17000, {
    ip_number: "6312345681",
    dispensary: "Erode",
    lwd: "2026-08-31",
    exit_reason: "Resignation",
  }),
  person("E004", "Priya N", "Production", "Erode Plant", "2023-03-06", 12500, { ip_number: "6312345673", dispensary: "Erode" }),
  person("E016", "Mohammed Ali", "Maintenance", "Erode Plant", "2019-10-01", 20000, { ip_number: "6312345674", dispensary: "Erode" }),
  person("E007", "Arun P", "HR", "Erode Office", "2021-08-16", 18500, { ip_number: "6312345675", dispensary: "Erode" }),
  person("E008", "Deepa S", "Finance", "Erode Office", "2022-12-01", 15500, { ip_number: "6312345676", dispensary: "Erode" }),
  person("E017", "Lakshmi V", "Stores", "Erode Plant", "2018-04-02", 21400, {
    ip_number: "6312345682",
    dispensary: "Erode",
    override: {
      status: "COVERED",
      from: "2026-04-01",
      reason: "Apr 2026 rate included a one-time arrear; regular wage ₹20,600 is within the ceiling.",
      saved_by: "Paramaguru",
      saved_at: "2026-03-28T12:10:00",
    },
  }),
];

const FIRST_NAMES = ["Arjun", "Bala", "Chitra", "Dinesh", "Elango", "Fathima", "Ganesh", "Hari", "Indira", "Jaya", "Kumar", "Latha", "Mani", "Nirmala", "Prakash", "Ramya", "Saravanan", "Thilaga", "Uma", "Vel"];
const LAST_INITIALS = ["A", "B", "C", "D", "G", "K", "M", "N", "P", "R", "S", "T", "V"];
const DEPARTMENTS = ["Production", "Production", "Quality", "Maintenance", "Stores", "HR", "Finance"];
const LOCATIONS = ["Erode Plant", "Erode Plant", "Erode Office", "Chennai Office"];

// 113 covered and 26 not-covered sample employees (6 covered at Coimbatore Depot,
// which has no ESI code), so the list shows the mockup's 156 / 128 / 28.
const fillers = () =>
  Array.from({ length: 139 }, (_, index) => {
    const covered = index < 113;
    const emp_id = `E${String(101 + index).padStart(3, "0")}`;
    const name = `${FIRST_NAMES[index % FIRST_NAMES.length]} ${LAST_INITIALS[(index * 7) % LAST_INITIALS.length]}`;
    const location = index >= 107 && index < 113 ? "Coimbatore Depot" : LOCATIONS[index % LOCATIONS.length];
    const doj = iso(moment("2017-01-02").add((index * 19) % 3000, "days"));
    const wage = covered ? 11000 + ((index * 137) % 98) * 100 : 21500 + ((index * 911) % 180) * 100;
    return person(emp_id, name, DEPARTMENTS[index % DEPARTMENTS.length], location, doj, wage, {
      ip_number: covered || index % 3 === 0 ? `63123${String(46000 + index).padStart(5, "0")}` : "",
      dispensary: location === "Chennai Office" ? "Ayanavaram" : location === "Coimbatore Depot" ? "Coimbatore" : "Erode",
    });
  });

export const sampleEmployees = () => JSON.parse(JSON.stringify([...NAMED, ...fillers()]));

// ---- Coverage rules (§4.2, first match wins) ----

export const wageOn = (employee, date) => {
  const applicable = employee.wages.filter((wage) => !day(wage.from).isAfter(day(date)));
  return (applicable[applicable.length - 1] || employee.wages[0]).amount;
};

// The disability flag in force on a date.
export const disabledOn = (employee, date) => Boolean(employee.disability && !day(employee.disability.from).isAfter(day(date)));

const latest = (...dates) => dates.filter(Boolean).sort().slice(-1)[0];

// { status, reason, from, until, rule, wage_at_test, test_label, ceiling }
// or null when the employee isn't employed in the period. Coverage uses the
// version in force at the period start for the whole period.
export const coverageFor = (employee, period) => {
  if (day(employee.doj).isAfter(day(period.end))) return null;
  if (employee.lwd && day(employee.lwd).isBefore(day(period.start))) return null;
  const settings = settingsOn(period.start);
  const ceiling = Number(settings.wage_ceiling);
  const disabilityCeiling = Number(settings.disability_ceiling) || 0;
  const joined = !day(employee.doj).isBefore(day(period.start));
  const testDate = joined ? employee.doj : period.start;
  const limitOn = (date) => (disabilityCeiling && disabledOn(employee, date) ? disabilityCeiling : ceiling);
  const limit = limitOn(testDate);
  const wageAtTest = wageOn(employee, testDate);
  const until = employee.lwd && !day(employee.lwd).isAfter(day(period.end)) ? employee.lwd : period.end;
  const left = until !== period.end;
  const base = {
    wage_at_test: wageAtTest,
    test_label: joined ? "Wage at joining" : "Wage at period start",
    ceiling: limit,
    settings,
  };
  const exitNote = left ? `Left on ${formatDay(employee.lwd)} (${employee.exit_reason || "exit"})` : `Re-tested on ${formatDay(moment(period.end).add(1, "day"))} at the new period start`;

  // 1. A saved override wins from its date.
  if (employee.override && !day(employee.override.from).isAfter(day(period.end))) {
    const covered = employee.override.status === "COVERED";
    return {
      ...base,
      status: covered ? "COVERED" : "NOT_COVERED",
      reason: "OVERRIDE",
      from: covered ? latest(period.start, employee.override.from, employee.doj) : null,
      until: covered ? until : null,
      rule: `Override from ${formatDay(employee.override.from)} — ${employee.override.reason}`,
      until_rule: covered ? exitNote : "",
    };
  }

  // 2–4. Period start / joiner, then mid-period rules. (An exit only ends
  // coverage on the last working day.)
  if (wageAtTest <= limit) {
    if (joined && settings.joiner_cover_from === "NEXT_PERIOD") {
      return { ...base, status: "NOT_COVERED", reason: "JOINER", from: null, until: null, rule: "New joiner — covered from the next period start (setting)" };
    }
    const rise = employee.wages.find((wage) => day(wage.from).isAfter(day(testDate)) && !day(wage.from).isAfter(day(until)) && wage.amount > limitOn(wage.from));
    if (rise && !settings.continue_to_period_end) {
      return {
        ...base,
        status: "COVERED",
        reason: joined ? "JOINER" : "PERIOD_START",
        from: testDate,
        until: iso(moment(rise.from).subtract(1, "day")),
        rule: joined ? "New joiner — from date of joining" : `Period start (wage ${money(wageAtTest)} within ceiling)`,
        until_rule: `Wage rose above the ceiling on ${formatDay(rise.from)}`,
      };
    }
    const disability = wageAtTest > ceiling;
    return {
      ...base,
      status: "COVERED",
      reason: rise ? "CONTINUING" : disability ? "DISABILITY" : joined ? "JOINER" : "PERIOD_START",
      from: testDate,
      until,
      rule: joined
        ? "Rule: new joiner — from date of joining"
        : disability
          ? `Rule: period start (wage ${money(wageAtTest)} within the disability ceiling ${money(limit)})`
          : `Rule: period start (wage ${money(wageAtTest)} within ceiling)`,
      until_rule: exitNote,
      rise,
    };
  }

  const drop = employee.wages.find((wage) => day(wage.from).isAfter(day(testDate)) && !day(wage.from).isAfter(day(until)) && wage.amount <= limitOn(wage.from));
  if (drop && settings.within_ceiling_cover_from === "THAT_MONTH") {
    return {
      ...base,
      status: "COVERED",
      reason: "WITHIN_CEILING_MIDPERIOD",
      from: iso(moment(drop.from).startOf("month")),
      until,
      rule: `Rule: wage fell within the ceiling in ${moment(drop.from).format("MMM YYYY")} — covered from that month`,
      until_rule: exitNote,
    };
  }
  return {
    ...base,
    status: "NOT_COVERED",
    reason: "PERIOD_START",
    from: null,
    until: null,
    rule: drop
      ? `Wage fell within the ceiling on ${formatDay(drop.from)} — covered from the next period start (setting)`
      : `${base.test_label} ${money(wageAtTest)} is above the ceiling ${money(limit)}`,
  };
};

export const COVERAGE_COLORS = {
  Covered: "#12B76A",
  "Covered · continuing": "#12B76A",
  "Covered · disability": "#12B76A",
  "Covered · override": "#6D5DF6",
  "Not covered": "#6B7280",
  "Not covered · override": "#6D5DF6",
};

export const coverageLabel = (coverage) => {
  if (!coverage) return "—";
  if (coverage.status === "NOT_COVERED") return coverage.reason === "OVERRIDE" ? "Not covered · override" : "Not covered";
  return (
    { CONTINUING: "Covered · continuing", DISABILITY: "Covered · disability", OVERRIDE: "Covered · override" }[coverage.reason] || "Covered"
  );
};

// IP status: OK / Due dd Mon (within 10 days of coverage start) / Missing.
export const ipStatusOf = (employee, coverage) => {
  if (!coverage || coverage.status !== "COVERED") return { label: "—", tone: null };
  if (employee.ip_number) return { label: "OK", tone: "green" };
  const due = moment(coverage.from).add(IP_DAYS, "days");
  return day(AS_OF).isAfter(due, "day")
    ? { label: "Missing", tone: "red", due: iso(due), overdue: true }
    : { label: `Due ${due.format("DD MMM")}`, tone: "amber", due: iso(due), overdue: false };
};

export const eligibilityRow = (employee, period) => {
  const coverage = coverageFor(employee, period);
  if (!coverage) return null;
  const code = codeForLocation(employee.location);
  const ip = ipStatusOf(employee, coverage);
  return {
    ...employee,
    coverage,
    code,
    code_label: codeName(code),
    ip_label: employee.ip_number || "—",
    wage_at_test: coverage.wage_at_test,
    coverage_label: coverageLabel(coverage),
    covered_until_label: coverage.until ? formatDay(coverage.until) : "—",
    ip_status: ip.label,
    ip,
  };
};

// Coverage history stepper: each period the employee was employed in,
// with "Joined" first when they joined in one of them.
export const coverageHistory = (employee) => {
  const steps = [];
  PERIODS.forEach((period) => {
    if (day(employee.doj).isAfter(day(period.end))) return;
    if (employee.lwd && day(employee.lwd).isBefore(day(period.start))) return;
    if (!day(employee.doj).isBefore(day(period.start))) steps.push({ label: "Joined", date: formatDay(employee.doj), done: true });
    if (!period.tested) {
      steps.push({ label: period.short, date: `Re-test ${moment(period.start).format("DD MMM")}`, done: false });
      return;
    }
    const coverage = coverageFor(employee, period);
    const label = coverageLabel(coverage);
    const date =
      coverage.status === "COVERED" && coverage.from !== period.start
        ? `Covered from ${moment(coverage.from).format("DD MMM")}`
        : { "Covered · continuing": "Covered (continuing)", "Covered · disability": "Covered (disability)", "Covered · override": "Covered (override)" }[label] || label;
    steps.push({ label: period.short, date, done: true });
  });
  return steps.slice(-3);
};

// ---- Edit ESI Details (E3) ----

export const OVERRIDE_OPTIONS = [
  { value: "SYSTEM", label: "Use system result" },
  { value: "COVERED", label: "Covered" },
  { value: "NOT_COVERED", label: "Not covered" },
];

export const nextPeriodStart = (period) => iso(moment(period.end).add(1, "day"));

// Field errors for the Edit ESI Details drawer. IP numbers are unique per
// company (AC-E07: the message names the employee who has it).
export const validateEsiDetails = (form, employee, employees, covered, disabilityShown) => {
  const errors = {};
  const ip = String(form.ip_number || "").trim();
  if (!ip) {
    if (covered) errors.ip_number = "Enter the IP number from the ESIC portal";
  } else if (!/^\d{10}$/.test(ip)) errors.ip_number = "10 digits";
  else {
    const other = employees.find((item) => item.id !== employee.id && item.ip_number === ip);
    if (other) errors.ip_number = `Already used by ${other.name} (${other.emp_id})`;
  }
  if (disabilityShown && form.disability_on) {
    if (!form.disability_from) errors.disability_from = "Pick the date it applies from";
    if (!form.disability_evidence) errors.disability_evidence = "Upload the certificate";
  }
  if (form.override_status !== "SYSTEM") {
    if (!form.override_from) errors.override_from = "Pick the date it applies from";
    else if (day(form.override_from).date() !== 1) errors.override_from = "Must be the 1st of a month";
    else if (day(form.override_from).isBefore(day(FIRST_OPEN_MONTH))) errors.override_from = `Payroll up to ${moment(FIRST_OPEN_MONTH).subtract(1, "month").format("MMM YYYY")} is finalised — pick ${formatDay(FIRST_OPEN_MONTH)} or later`;
    if (String(form.override_reason || "").trim().length < 5) errors.override_reason = "Give the reason (it is audited and shown in Validation)";
  }
  return errors;
};

// ---- Import IP numbers ----

export const IP_IMPORT_FIELDS = [
  { key: "employee_id", label: "Employee ID", required: true, note: "Yes", match: ["employee id", "emp id", "empid", "employee code", "emp code", "id"] },
  { key: "employee_name", label: "Employee Name", note: "No (for checking)", match: ["employee name", "name", "emp name"] },
  { key: "ip_number", label: "IP Number", required: true, note: "Yes · 10 digits", match: ["ip number", "ip no", "ip", "insurance number", "esi number", "esic ip number"] },
  { key: "dispensary", label: "Dispensary", note: "No", match: ["dispensary", "esi dispensary"] },
  {
    key: "covered_in_current_period",
    label: "Covered in Current Period",
    note: "No · Yes / No (go-live only)",
    match: ["covered in current period", "covered in period", "covered", "esi covered"],
  },
];

export const normalise = (text) => String(text ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export const autoMapIpColumns = (headers) => {
  const mapping = {};
  const used = new Set();
  headers.forEach((header, index) => {
    const field = IP_IMPORT_FIELDS.find((item) => !used.has(item.key) && item.match.includes(normalise(header)));
    if (field) {
      mapping[index] = field.key;
      used.add(field.key);
    }
  });
  return mapping;
};

export const missingIpFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IP_IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// Checks every file row against the employees: Valid (new IP), Change
// (replaces an IP), No change (same as saved, so a second import changes
// nothing) or Error.
export const checkIpRows = (rows, mapping, employees) => {
  const column = (key) => Object.keys(mapping).find((index) => mapping[index] === key);
  const read = (row, key) => {
    const index = column(key);
    return index == null ? "" : String(row[index] ?? "").trim();
  };
  const seenIp = {};
  return rows.map((row, index) => {
    const empId = read(row, "employee_id").toUpperCase();
    const ip = read(row, "ip_number").replace(/\s/g, "");
    const dispensary = read(row, "dispensary");
    const employee = employees.find((item) => item.emp_id.toUpperCase() === empId);
    const result = { key: index, employee_id: empId || "—", name: employee?.name || read(row, "employee_name") || "—", ip_number: ip || "—", dispensary, employee };
    const error = (message) => ({ ...result, result: "Error", message });
    if (!empId) return error("Employee ID is empty");
    if (!employee) return error("No employee with this ID");
    if (!/^\d{10}$/.test(ip)) return error("IP number must be 10 digits");
    const other = employees.find((item) => item.id !== employee.id && item.ip_number === ip);
    if (other) return error(`IP number already belongs to ${other.name} (${other.emp_id})`);
    if (seenIp[ip] && seenIp[ip] !== employee.id) return error("Same IP number on another row of this file");
    seenIp[ip] = employee.id;
    if (employee.ip_number === ip && (!dispensary || dispensary === employee.dispensary)) return { ...result, result: "No change", message: "Already saved" };
    if (employee.ip_number && employee.ip_number !== ip) return { ...result, result: "Change", message: `Replaces ${employee.ip_number}` };
    return { ...result, result: "Valid", message: employee.ip_number ? "Dispensary updated" : "IP number added" };
  });
};

// Template: covered employees of the current period, current IP filled in.
export const ipTemplateRows = (employees) =>
  employees
    .map((employee) => eligibilityRow(employee, periodOf(CURRENT_PERIOD)))
    .filter((row) => row && row.coverage.status === "COVERED")
    .map((row) => [row.emp_id, row.name, row.ip_number, row.dispensary]);
